import { FC } from "react";
import FormSet, { FormSetDescription, FormSetTitle } from "./FormSet";
import { useMutation } from "@apollo/client/react";
import {
  TranslationInput,
  TextPropsFragment,
  UpdateTextInput,
  AddDescriptionDocument,
  DeleteDescriptionDocument,
  UpdateDescriptionDocument,
} from "../../generated/graphql";
import { useSnackbar } from "notistack";
import TranslationFormSet from "./TranslationFormSet";
import { styled } from "@mui/material/styles";
import { T } from "@tolgee/react";
import { Box } from "@mui/material";

// Replace makeStyles with styled component
const StyledFormSetDescription = styled(FormSetDescription)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

// Container for the description form to ensure proper width
const DescriptionFormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  '& .MuiFormControl-root': {
    width: '100%',
  },
  '& .MuiInputBase-root': {
    width: '100%',
  },
  // Make the translation form fields wider to utilize available space
  '& .MuiGrid-container': {
    width: '100%',
  },
  // Ensure the text field has enough space for the button
  '& .MuiGrid-item': {
    '&:first-of-type': {
      flex: 1, // Take all available space
      minWidth: '60%', // Ensure minimum width
    }
  }
}));

type DescriptionFormSetProps = {
  catalogEntryId: string;
  descriptions: TextPropsFragment[];
  refetch: () => Promise<any>;
};

const DescriptionFormSet: FC<DescriptionFormSetProps> = (props) => {
  const { catalogEntryId, descriptions, refetch } = props;

  const { enqueueSnackbar } = useSnackbar();
  const [addDescription] = useMutation(AddDescriptionDocument);
  const [updateDescription] = useMutation(UpdateDescriptionDocument);
  const [deleteDescription] = useMutation(DeleteDescriptionDocument);

  const handleOnAdd = async (text: TranslationInput) => {
    await addDescription({
      variables: {
        input: { catalogEntryId, text },
      },
    });
    enqueueSnackbar("Beschreibung hinzugefügt.");
    await refetch();
  };

  const handleOnUpdate = async (description: UpdateTextInput) => {
    await updateDescription({
      variables: {
        input: description,
      },
    });
    enqueueSnackbar("Beschreibung aktualisiert.");
  };

  const handleOnDelete = async (textId: string) => {
    await deleteDescription({
      variables: {
        input: { textId },
      },
    });
    enqueueSnackbar("Beschreibung gelöscht.");
    await refetch();
  };

  return (
    <FormSet>
      <FormSetTitle>
        <b>
          <T keyName={"description.title"} />
        </b>
      </FormSetTitle>
      <StyledFormSetDescription>
        <T keyName={"description.description"} />
      </StyledFormSetDescription>

      <div style={{ marginBottom: "12px" }}></div>

      <DescriptionFormContainer>
        <TranslationFormSet
          label="Beschreibung"
          translations={descriptions}
          min={0}
          onAdd={handleOnAdd}
          onUpdate={handleOnUpdate}
          onDelete={handleOnDelete}
          TextFieldProps={{
            multiline: true,
            maxRows: 10,
            fullWidth: true, // Ensure the text field takes full width
            sx: { flexGrow: 1 } // Grow to fill available space
          }}
          // The width is already handled by DescriptionFormContainer
        />
      </DescriptionFormContainer>
    </FormSet>
  );
};

export default DescriptionFormSet;
